<script setup lang="ts">
import { computed, ref } from "vue";
import { FormInput, Checkbox, Submit } from "@/ts/components/form";
import { CalendarEvent } from "@/ts/stores/event";
import { useForm } from "vee-validate";
import * as Yup from "yup";
import { useErrors } from "@/ts/utils/api-functions";
import { trans, wTrans } from "laravel-vue-i18n";
import { useReCaptcha } from "vue-recaptcha-v3";
import { serialize } from "object-to-formdata";
import axios from "axios";
import { dateString } from "@/ts/utils/event-utils";

interface Props {
    event: CalendarEvent;
}

const props = defineProps<Props>();

const apiErrors = useErrors();
const reCaptcha = useReCaptcha();
const success = ref(false);
const loading = ref(false);
const extraPersons = ref<{ full_name?: string; age?: number }[]>([
    {
        full_name: undefined,
        age: undefined,
    },
]);

const emit = defineEmits(["submit", "back", "mounted"]);

const schema = computed(() =>
    Yup.object({
        event_name: Yup.string().required(),
        event_date: Yup.string().required(),
        confirmation_email: Yup.string().email().required(),
        full_name: Yup.string().required().label(wTrans("event.registration_form.full_name").value),
        age: Yup.number().required().label(wTrans("event.registration_form.age").value),
        email: Yup.string().email().required().label(wTrans("event.registration_form.email").value),
        privacy: Yup.bool().oneOf([true]).label(wTrans("event.registration_form.privacy_validation").value),
        extra_persons: Yup.array()
            .of(
                Yup.object({
                    full_name: Yup.string().label(wTrans("event.registration_form.full_name").value),
                    age: Yup.number().min(0).label(wTrans("event.registration_form.age").value),
                }),
            )
            .test({
                name: "Incomplete extra persons data",
                message: "Gelieve de ontbrekende gegevens in te vullen.",
                test: (extra_person) => {
                    if (extra_person) {
                        return extra_person.every(({ full_name, age }, index) => {
                            if (!age && (!full_name || full_name === "") && index !== extraPersons.value.length - 1) {
                                return false;
                            }
                            if (age && (!full_name || full_name === "")) {
                                return false;
                            }
                            if (full_name && full_name !== "" && !age) {
                                return false;
                            }
                            return true;
                        });
                    }
                    return true;
                },
            }),
    }),
);

const { handleSubmit, errors, values, submitCount, isSubmitting } = useForm({
    initialValues: {
        event_name: props.event.title.rendered,
        event_date: dateString(props.event),
        confirmation_email: props.event.acf.info.confirmation_email,
        full_name: undefined,
        age: undefined,
        email: undefined,
        privacy: false,
        extra_persons: [],
    },
    validationSchema: schema,
});

const onSubmit = handleSubmit(async (values, { resetForm }) => {
    loading.value = true;
    try {
        await reCaptcha!.recaptchaLoaded();
        const token = await reCaptcha!.executeRecaptcha("login");
        const formData = serialize(values, { indices: true });
        formData.append("g-recaptcha-response", token);
        formData.append("_wpcf7_unit_tag", "9226af4");
        await axios.post("/api/event-registration", formData);
        success.value = true;
        emit("submit", values);
    } catch (error) {
        apiErrors.parse(error);
    } finally {
        loading.value = false;
    }
});

function modifyExtraArray(index: number) {
    const editedEntry = extraPersons.value[index];
    editedEntry.full_name = values.extra_persons![index].full_name;
    editedEntry.age = values.extra_persons![index].age;

    const emptySlotAvailable =
        extraPersons.value.length > 1 &&
        extraPersons.value.find(({ full_name, age }) => {
            if ((!full_name || full_name !== "") && !age) {
                return true;
            }
            return false;
        });

    if ((!editedEntry.full_name || editedEntry.full_name === "") && !editedEntry.age) {
        extraPersons.value.splice(index, 1);
    } else if (!emptySlotAvailable && editedEntry && editedEntry.full_name && editedEntry.full_name !== "" && editedEntry.age) {
        extraPersons.value.push({
            full_name: undefined,
            age: undefined,
        });
    }
}
</script>

<template>
    <transition-group name="fade" mode="out-in">
        <form v-if="!success" @submit="onSubmit">
            <h2 class="mb-6 font-heading text-32 font-[475] [font-stretch:_190%]">Schrijf je nu in!</h2>
            <div class="mb-10 grid grid-cols-4 gap-4">
                <form-input class="col-span-4 md:col-span-2" name="full_name" :auto-focus="true" :placeholder="trans('event.registration_form.full_name')" :label="trans('event.registration_form.full_name')"></form-input>
                <form-input class="col-span-4 md:col-span-2" name="age" type="number" :auto-focus="true" :placeholder="trans('event.registration_form.age')" :label="trans('event.registration_form.age')"></form-input>
                <form-input class="col-span-4 md:col-span-2" name="email" type="email" :auto-focus="true" :placeholder="trans('event.registration_form.email')" :label="trans('event.registration_form.email')"></form-input>
            </div>
            <h3 class="mb-4 leading-normal">Extra personen</h3>
            <div class="mb-10 grid grid-cols-4 gap-4">
                <transition-group name="fade">
                    <template :key="index" v-for="index in extraPersons.keys()">
                        <form-input class="col-span-4 md:col-span-2" @input="modifyExtraArray(index)" :name="`extra_persons[${index}].full_name`" :placeholder="trans('event.registration_form.full_name')" :label="index === 0 ? trans('event.registration_form.full_name') : ''"></form-input>
                        <form-input class="col-span-4 md:col-span-2" @input="modifyExtraArray(index)" :name="`extra_persons[${index}].age`" type="number" :min="0" :placeholder="trans('event.registration_form.age')" :label="index === 0 ? trans('event.registration_form.age') : ''"></form-input>
                    </template>
                </transition-group>
                <form-input class="col-span-4 md:col-span-2" :disabled="true" :name="`extra_persons.ghost.first_name`" :placeholder="trans('event.registration_form.full_name')"></form-input>
                <form-input class="col-span-4 md:col-span-2" :disabled="true" :name="`extra_persons.ghost.age`" type="number" :placeholder="trans('event.registration_form.age')"></form-input>
                <div class="bg-red-100 z-[1] col-span-4 mt-2 flex items-center rounded-[8px] text-14" v-if="submitCount > 1 && errors.extra_persons">
                    <span class="text-14 text-red">{{ errors.extra_persons }}</span>
                </div>
            </div>
            <checkbox class="mt-10" name="privacy">
                <span v-html="trans('event.registration_form.privacy_policy')"></span>
            </checkbox>
            <div class="col-span-4 flex md:mt-10 md:flex-col md:items-start">
                <submit class="btn btn-green w-full md:w-auto" :disabled="success" :loading="isSubmitting || loading">
                    {{ trans("event.registration_form.confirm") }}
                    <svg-vue icon="arrow" class="ml-[.625rem] h-6 w-auto"></svg-vue>
                </submit>
                <component class="mt-10" :is="apiErrors.render()" :data="apiErrors.get()"></component>
            </div>
        </form>
        <p v-else class="font-bold text-20 p-4 bg-gray-300 text-center">Gelukt! We hebben je inschrijving goed ontvangen.</p>
    </transition-group>
</template>
